/**
 * Deterministic color assignment for analytics chart data series.
 * Colors are keyed by dimensionValue (series key), ensuring the same color
 * is used regardless of chart type. Uses a fixed palette derived from
 * Tailwind theme tokens.
 */

/** Fixed palette of distinguishable colors from the Tailwind theme */
const CHART_COLORS = [
  '#2563eb', // blue-600
  '#dc2626', // red-600
  '#16a34a', // green-600
  '#9333ea', // purple-600
  '#ea580c', // orange-600
  '#0891b2', // cyan-600
  '#ca8a04', // yellow-600
  '#db2777', // pink-600
  '#4f46e5', // indigo-600
  '#059669', // emerald-600
  '#7c3aed', // violet-600
  '#d97706', // amber-600
] as const;

/** SVG pattern IDs for accessibility — supplements color with visual patterns */
export const CHART_PATTERNS = [
  'pattern-solid',
  'pattern-diagonal',
  'pattern-horizontal',
  'pattern-vertical',
  'pattern-dots',
  'pattern-crosshatch',
  'pattern-zigzag',
  'pattern-diamond',
  'pattern-wave',
  'pattern-grid',
  'pattern-circles',
  'pattern-triangles',
] as const;

/**
 * Simple hash function for strings.
 * Produces a consistent non-negative integer for any string input.
 */
function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash + char) | 0;
  }
  return Math.abs(hash);
}

/**
 * Returns a deterministic color for a given series key (dimensionValue).
 * The same key always produces the same color regardless of chart type or context.
 */
export function getSeriesColor(dimensionValue: string): string {
  const index = hashString(dimensionValue) % CHART_COLORS.length;
  return CHART_COLORS[index] ?? CHART_COLORS[0];
}

/**
 * Returns a deterministic pattern ID for a given series key.
 * Used alongside color for accessibility (patterns supplement color).
 */
export function getSeriesPattern(dimensionValue: string): string {
  const index = hashString(dimensionValue) % CHART_PATTERNS.length;
  return CHART_PATTERNS[index] ?? CHART_PATTERNS[0];
}

/**
 * Returns both color and pattern for a series key.
 */
export function getSeriesStyle(dimensionValue: string): { color: string; patternId: string } {
  return {
    color: getSeriesColor(dimensionValue),
    patternId: getSeriesPattern(dimensionValue),
  };
}
